/**
 * /linkages/new and /linkages/:key — a linkage template drawn on a canvas
 * (Feature 71). The page holds the draft; the canvas and the inspector edit
 * it; a save sends the whole template, its layout included (D36).
 *
 * The agent judges a save whole: a refused one answers every problem at
 * once, each on a step, a `from→to` link, or the graph, and the canvas marks
 * the node or edge each names. A new template, once saved, lands on its own
 * key; its key is fixed from then on (D19). A new template's key is made
 * from its name (Feature 73 D7), and a name outside the rule is refused in
 * the form before anything is sent (D8).
 *
 * How it works walks the editor in a guided tour, which opens by itself on
 * this browser's first visit (Feature 72 D6, D12). A handle between the
 * canvas and the inspector sets the inspector's width (`InspectorSplit`).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { ApiError } from '@optum/api-client/client';
import { listCapabilities } from '@optum/api-client/endpoints/capabilities';
import {
  createLinkageTemplate,
  getLinkageTemplate,
  listLinkageTemplates,
  replaceLinkageTemplate,
} from '@optum/api-client/endpoints/linkage-templates';
import type { LinkageProblem } from '@optum/api-client/types';

import { useBreadcrumbTail } from '../../components/shell/BreadcrumbTailContext.js';
import { qk } from '../../lib/query-keys.js';
import { useUnsavedChanges } from '../../lib/unsaved.js';
import { ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Button } from '../../ui/Button.js';
import { Banner } from '../../ui/finixt.js';
import { FlowProvider } from '../../ui/flow.js';
import { FormShape } from '../../ui/shapes.js';
import { Tour } from '../../ui/Tour.js';
import { useToast } from '../../ui/useToast.js';
import { callProblems } from './call.js';
import { gateProblems } from './decisions.js';
import { firstProblems } from './first.js';
import { positionProblems } from './positions.js';
import { emptyDraft, fromTemplate, sameDraft, toBody, type Draft } from './draft.js';
import { InspectorSplit } from './InspectorSplit.js';
import { LinkageCanvas } from './LinkageCanvas.js';
import { LinkageFields } from './LinkageFields.js';
import { LinkageInspector } from './LinkageInspector.js';
import { AUTOMATION_PATH } from '../automation/paths.js';
import { nameProblem } from './key.js';
import { canvasPath } from './LinkageRecord.js';
import type { Selection } from './selection.js';
import { linkageTourSteps, useLinkageTour } from './tour.js';
import { useCapabilitySchemas } from './useCapabilitySchemas.js';

/** The problems a refused save carried, or none for any other error. */
export function savedProblems(error: unknown): LinkageProblem[] {
  if (!(error instanceof ApiError)) return [];
  const problems = error.details?.['problems'];
  return Array.isArray(problems) ? (problems as LinkageProblem[]) : [];
}

/** The gate or step open in the inspector, for its width to fit; null when none is. */
function fitKeyOf(selection: Selection): string | null {
  if (selection === null) return null;
  return selection.kind === 'node'
    ? `node:${selection.id}`
    : `edge:${selection.from}→${selection.to}`;
}

function Editor({ initial, isNew }: { initial: Draft; isNew: boolean }) {
  const navigate = useNavigate();
  const toast = useToast();
  const qc = useQueryClient();
  const [draft, setDraft] = useState(initial);
  const [saved, setSaved] = useState(initial);
  const [problems, setProblems] = useState<LinkageProblem[]>([]);
  const [selection, setSelection] = useState<Selection>(null);
  const release = useUnsavedChanges(!sameDraft(draft, saved));
  const tour = useLinkageTour();
  const schemas = useCapabilitySchemas([
    draft.firstCapabilityId,
    ...draft.steps.map((s) => s.capability_id),
  ]);

  // What the agent would refuse of a call template, a gate that follows
  // nothing yet, a missing first node or schedule input, and a reference that
  // reads a list by position, marked before a save so Save waits (Feature 78
  // D18, Feature 79 D29, Feature 80 D20, Feature 82 D6); a
  // refused save's problems stand beside them.
  const standing = useMemo(
    () => [
      ...callProblems(draft),
      ...gateProblems(draft),
      ...firstProblems(draft, schemas),
      ...positionProblems(draft),
    ],
    [draft, schemas],
  );
  const shown = useMemo(
    () => [
      ...problems,
      ...standing.filter(
        (p) => !problems.some((q) => q.where === p.where && q.message === p.message),
      ),
    ],
    [problems, standing],
  );

  const capabilities = useQuery({ queryKey: qk.capabilities(), queryFn: listCapabilities });
  const options = useMemo(
    () =>
      (capabilities.data ?? [])
        .filter((c) => c.status === 'active')
        .map((c) => ({ value: c.id, label: `${c.id} — ${c.name}` })),
    [capabilities.data],
  );
  const templates = useQuery({ queryKey: qk.linkageTemplates(), queryFn: listLinkageTemplates });
  const takenKeys = useMemo(
    () => new Set((templates.data ?? []).map((t) => t.key).filter((key) => key !== saved.key)),
    [templates.data, saved.key],
  );

  /** Save, unless the name breaks the rule: that is said in the form, not sent. */
  function onSave() {
    const problem = nameProblem(draft.name);
    if (problem) {
      toast.error('Not saved', `The name: ${problem}`);
      return;
    }
    save.mutate();
  }

  const save = useMutation({
    mutationFn: () =>
      isNew
        ? createLinkageTemplate(toBody(draft))
        : replaceLinkageTemplate(draft.key, toBody(draft)),
    onSuccess: (template) => {
      setProblems([]);
      void qc.invalidateQueries({ queryKey: qk.linkageTemplates() });
      toast.success(`Saved ${template.key}`);
      const next = fromTemplate(template);
      setSaved(next);
      setDraft(next);
      if (isNew) {
        release();
        navigate(canvasPath(template.key), { replace: true });
      }
    },
    onError: (e) => {
      const found = savedProblems(e);
      setProblems(found);
      toast.error(
        'Not saved',
        found.length > 0
          ? `${found.length} problem${found.length === 1 ? '' : 's'}; they are marked on the canvas.`
          : e instanceof ApiError
            ? e.message
            : String(e),
      );
    },
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingBottom: 64 }}>
      <LinkageFields draft={draft} isNew={isNew} takenKeys={takenKeys} onChange={setDraft} />
      {problems.length > 0 && (
        <Banner
          tone="danger"
          title={`Not saved: ${problems.length} problem${problems.length === 1 ? '' : 's'}`}
        >
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {problems.map((p) => (
              <li key={`${p.where}:${p.code}:${p.message}`}>
                <span className="mono">{p.where}</span> · {p.message}
              </li>
            ))}
          </ul>
        </Banner>
      )}
      {standing.length > 0 && (
        <Banner
          tone="warn"
          title={`Save waits: ${standing.length} problem${standing.length === 1 ? '' : 's'} the agent would refuse`}
        >
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {standing.map((p) => (
              <li key={`${p.where}:${p.code}:${p.message}`}>
                <span className="mono">{p.where}</span> · {p.message}
              </li>
            ))}
          </ul>
        </Banner>
      )}
      <FlowProvider>
        <InspectorSplit
          fitKey={fitKeyOf(selection)}
          canvas={
            <LinkageCanvas
              draft={draft}
              problems={shown}
              selection={selection}
              capabilities={options}
              schemas={schemas}
              onChange={setDraft}
              onSelect={setSelection}
              onTour={tour.start}
            />
          }
          inspector={
            <LinkageInspector
              draft={draft}
              isNew={isNew}
              selection={selection}
              capabilities={options}
              schemas={schemas}
              onChange={setDraft}
              onSelect={setSelection}
            />
          }
        />
      </FlowProvider>
      <div
        style={{
          display: 'flex',
          gap: 7,
          paddingTop: 16,
          borderTop: '1px solid var(--border-hairline)',
        }}
      >
        <Button
          variant="primary"
          data-tour="save"
          disabled={save.isPending || standing.length > 0}
          onClick={onSave}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="ghost" onClick={() => navigate(AUTOMATION_PATH)}>
          Cancel
        </Button>
      </div>
      <Tour
        label="How it works"
        steps={linkageTourSteps(draft, setSelection)}
        open={tour.open}
        onClose={tour.close}
      />
    </div>
  );
}

export function LinkageEditorPage() {
  const { key } = useParams<{ key?: string }>();
  const template = useQuery({
    queryKey: qk.linkageTemplate(key ?? ''),
    queryFn: () => getLinkageTemplate(key ?? ''),
    enabled: key !== undefined,
  });
  useBreadcrumbTail(key ? (template.data?.name ?? key) : 'New linkage');
  const initial = useMemo(
    () => (template.data ? fromTemplate(template.data) : emptyDraft()),
    [template.data],
  );
  const [loadedKey, setLoadedKey] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (template.data) setLoadedKey(template.data.key);
  }, [template.data]);

  return (
    <FormShape
      wide
      title={key ? (template.data?.name ?? key) : 'New linkage'}
      subtitle="Steps are nodes and links are edges. Each link carries a gate: conditions on what came before it, or pass automatically. Each step maps earlier inputs and results into its own. A save is judged whole."
    >
      {key && template.isPending ? (
        <LoadingBlock />
      ) : key && template.isError ? (
        <ErrorBlock
          message={template.error instanceof Error ? template.error.message : 'Failed to load'}
        />
      ) : (
        <Editor key={loadedKey ?? 'new'} initial={initial} isNew={key === undefined} />
      )}
    </FormShape>
  );
}
